import React, { KeyboardEvent } from 'react';

interface Step5GeneralOptionsProps {
  minLikes: string;
  maxLikes: string;
  minPrice: string;
  maxPrice: string;
  trendingOnly: boolean;
  maxPages: string;
  mustInclude: string[];
  mustExclude: string[];
  onFieldChange: (field: string, value: any) => void;
  isUnlimited?: boolean;
  userPlan?: string;
}

export const Step5GeneralOptions: React.FC<Step5GeneralOptionsProps> = ({
  minLikes,
  maxLikes,
  minPrice,
  maxPrice,
  trendingOnly,
  maxPages,
  mustInclude,
  mustExclude,
  onFieldChange,
  isUnlimited = false,
  userPlan = 'free',
}) => {
  const maxAllowed = isUnlimited ? undefined : 5;
  const maxLabel = isUnlimited ? 'illimité' : '5';
  const [currentIncludeWord, setCurrentIncludeWord] = React.useState('');
  const [currentExcludeWord, setCurrentExcludeWord] = React.useState('');

  const handleAddIncludeWord = () => {
    if (currentIncludeWord.trim()) {
      onFieldChange('mustInclude', [...mustInclude, currentIncludeWord.trim()]);
      setCurrentIncludeWord('');
    }
  };

  const handleAddExcludeWord = () => {
    if (currentExcludeWord.trim()) {
      onFieldChange('mustExclude', [...mustExclude, currentExcludeWord.trim()]);
      setCurrentExcludeWord('');
    }
  };

  const handleRemoveIncludeWord = (word: string) => {
    onFieldChange('mustInclude', mustInclude.filter(w => w !== word));
  };

  const handleRemoveExcludeWord = (word: string) => {
    onFieldChange('mustExclude', mustExclude.filter(w => w !== word));
  };

  const handleIncludeKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddIncludeWord();
    }
  };

  const handleExcludeKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddExcludeWord();
    }
  };

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-900 mb-4">Options de recherche</h3>

      {/* Fourchette de prix */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          💰 Fourchette de prix (€)
        </label>
        <div className="grid grid-cols-2 gap-4">
          <input
            type="number"
            value={minPrice}
            onChange={(e) => onFieldChange('minPrice', e.target.value)}
            placeholder="Prix min"
            min="0"
            step="0.01"
            className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500"
          />
          <input
            type="number"
            value={maxPrice}
            onChange={(e) => onFieldChange('maxPrice', e.target.value)}
            placeholder="Prix max"
            min="0"
            step="0.01"
            className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500"
          />
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Seuls les articles dans cette fourchette seront récupérés
        </p>
      </div>

      {/* Nombre de likes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          ❤️ Nombre de likes
        </label>
        <div className="grid grid-cols-2 gap-4">
          <input
            type="number"
            value={minLikes}
            onChange={(e) => onFieldChange('minLikes', e.target.value)}
            placeholder="Min"
            className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500"
          />
          <input
            type="number"
            value={maxLikes}
            onChange={(e) => onFieldChange('maxLikes', e.target.value)}
            placeholder="Max"
            className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500"
          />
        </div>
      </div>

      {/* Nombre de pages */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Nombre de pages à scraper
        </label>
        <input
          type="number"
          value={maxPages}
          onChange={(e) => onFieldChange('maxPages', e.target.value)}
          placeholder={isUnlimited ? '100' : maxAllowed?.toString()}
          min="1"
          {...(maxAllowed !== undefined ? { max: maxAllowed } : {})}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500"
        />
        <p className="text-xs text-gray-500 mt-1">
          Jusqu'à {maxLabel} pages par recherche.
        </p>
      </div>

      {/* Mots-clés à inclure */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Mots-clés obligatoires (dans titre)
        </label>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={currentIncludeWord}
            onChange={(e) => setCurrentIncludeWord(e.target.value)}
            onKeyPress={handleIncludeKeyPress}
            placeholder="Ex: vintage"
            className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500"
          />
          <button
            type="button"
            onClick={handleAddIncludeWord}
            className="px-6 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors font-medium"
          >
            Ajouter
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {mustInclude.map(word => (
            <span
              key={word}
              className="px-3 py-1 bg-green-100 text-green-800 rounded-lg text-sm flex items-center gap-2"
            >
              {word}
              <button
                type="button"
                onClick={() => handleRemoveIncludeWord(word)}
                className="text-green-600 hover:text-green-800"
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* Mots-clés à exclure */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Mots-clés à exclure (dans titre)
        </label>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={currentExcludeWord}
            onChange={(e) => setCurrentExcludeWord(e.target.value)}
            onKeyPress={handleExcludeKeyPress}
            placeholder="Ex: abîmé"
            className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500"
          />
          <button
            type="button"
            onClick={handleAddExcludeWord}
            className="px-6 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors font-medium"
          >
            Ajouter
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {mustExclude.map(word => (
            <span
              key={word}
              className="px-3 py-1 bg-red-100 text-red-800 rounded-lg text-sm flex items-center gap-2"
            >
              {word}
              <button
                type="button"
                onClick={() => handleRemoveExcludeWord(word)}
                className="text-red-600 hover:text-red-800"
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};
